////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// turn-widget-gm-controls.ts — TypeScript port of includes/js/turn-widget-gm-controls.js
// ----------------------------------------------------------------------------------------------------
// Bundled into includes/js/dist/turn-widget-bundle.js via tools/build/build-turn-widget-bundle.mjs.
// index.ts publishes the renderer and dependency patcher to the side-panels-init bundle.
// The PHP partial and original JavaScript renderer are retired; held browser state owns the controls.
////////////////////////////////////////////////////////////////////////////////////////////////////////////////

import type { EncounterArr, CampaignArr, PictureRow } from '../shared/array-types';
import { encounterCreatureField, encounterField, picturesOf } from '../shared/array-types';
import { parseText, ftMountParseTextTables } from '../shared/parse-text';
import { ftSetCampaignClockRunning } from '../shared/campaign-clock-transport';
import { hydrateSubtree } from '../shared/hydrate-subtree';
import { replaceHydratedHtml } from '../shared/replace-hydrated-html';
import { downloadHeldEncounterArray } from './encounter-array-download';

type AnyWindow = Record<string, unknown>;

type EncounterContentField = 'encounter_description' | 'encounter_gm_notes';

interface EncounterContentSnapshot {
    encounter_id: string;
    encounter_description: string;
    encounter_gm_notes: string;
}

// Row 1 is built from the already-loaded encounter_arr. Keep the two prose fields beside the
// rendered controls so opening either panel is a local render, not a second request for data the
// browser already owns. Updating the entry when either field changes keeps WS/cache refreshes
// authoritative without embedding large rich-text values in data attributes.
let _ftTwgEncounterContent: EncounterContentSnapshot | null = null;

function _ftTwgCall(name: string, ...args: unknown[]): unknown {
    const fn = (window as unknown as AnyWindow)[name];
    return typeof fn === 'function' ? fn(...args) : undefined;
}

async function _ftTwgQueryData(url: string, target: string, payload: Record<string, unknown>): Promise<void> {
    if (/\/toggle_(?:campaign_downtime|hunger_thirst)_enabled_inc\.php$/.test(url)) {
        const prepare = (window as unknown as { ftPrepareCampaignNeedsToggle?: (url: string, data: Record<string, unknown>) => Promise<boolean> }).ftPrepareCampaignNeedsToggle;
        if (!prepare) {
            _ftTwgCall('displayWarning', 'Campaign toggle unavailable: browser planner is not loaded.');
            return;
        }
        if (!await prepare(url, payload)) return;
    }
    // These commands paint only their warning/result host. Keep queryData's document pass and
    // unfocus disabled: neither the retained controls nor the rest of the page was inserted.
    const result = await _ftTwgCall('queryData', url, target, payload, false, true, false);
    const host = document.getElementById(target);
    if (host) hydrateSubtree(host);
    if (result == null) _ftTwgCall('displayWarning', 'GM control request failed; campaign controls retain the last received state.');
}

async function _ftTwgQueryDataInputs(url: string, target: string, payload: Record<string, unknown>): Promise<void> {
    await _ftTwgCall('queryDataInputs', url, target, payload);
}


document.addEventListener('click', (event) => {
    const el = event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-ft-turn-widget-action]')
        : null;
    if (!el) return;
    event.preventDefault();
    const action = el.dataset.ftTurnWidgetAction || '';
    if (action === 'download-encounter-array' && el instanceof HTMLButtonElement) {
        void downloadHeldEncounterArray(el, el.dataset.ftEncounterId || '');
        return;
    }
    if (action === 'clear-caster') {
        _ftTwgCall('clearCaster');
        return;
    }
    if (action === 'show-encounter-content') {
        const encounterId = el.dataset.ftEncounterId || '';
        const field = el.dataset.ftEncounterContentField as EncounterContentField | undefined;
        const snapshot = _ftTwgEncounterContent;
        if (
            !snapshot
            || snapshot.encounter_id !== encounterId
            || (field !== 'encounter_description' && field !== 'encounter_gm_notes')
        ) return;

        const host = document.getElementById(el.dataset.ftTarget || 'div_refresh_gm_viewer');
        if (!host) return;

        const isGmNotes = field === 'encounter_gm_notes';
        const panelClass = isGmNotes ? 'ft-lore-panel--gm' : 'ft-lore-panel--read-aloud';
        const iconKey = isGmNotes ? 'encounter_gm_notes' : 'encounter_description';
        const label = isGmNotes ? 'Encounter GM Notes' : 'Encounter Description';
        host.innerHTML = `
            <div class="cl_encounter_gm_notes ft-lore-panel ${panelClass} mb-2">
                <div class="ft-lore-kicker">${iconMap(iconKey, 18, 18, false)} ${label}</div>
                <div>${parseText(snapshot[field])}</div>
            </div>`;
        // parseText keeps [table:] / [random_table:] as lazy data mounts. Hydrate only those nested
        // references; the encounter prose itself came directly from encounter_arr above.
        ftMountParseTextTables(host);
        return;
    }
    if (action === 'viewer-inventory') {
        // FT3D campaign shell (owner 2026-09-16): inventory is added from Edit Scene › More › Gameplay
        // Inventory. The shell hides the floating windows and enters Edit Scene; routes without the
        // shell keep loading the panel into the GM viewer below.
        const openEditTool = (window as unknown as AnyWindow).ftOpenFt3dEditTool;
        if (typeof openEditTool === 'function') {
            (openEditTool as (tool: string) => void)('inventory');
            return;
        }
    }
    if (action === 'viewer-inventory' || action === 'viewer-random') {
        const inventoryBtn = document.getElementById('turn_widget_btn_gm_viewer_inventory');
        const randomBtn = document.getElementById('turn_widget_btn_gm_viewer_random');
        if (inventoryBtn) inventoryBtn.className = action === 'viewer-inventory' ? 'btn btn-sm btn-primary' : 'btn btn-sm btn-outline-primary';
        if (randomBtn) randomBtn.className = action === 'viewer-random' ? 'btn btn-sm btn-primary' : 'btn btn-sm btn-outline-primary';
        void _ftTwgQueryDataInputs(el.dataset.ftUrl || '', 'div_refresh_gm_viewer', {
            campaign_id: 'campaign_id',
            encounter_id: 'encounter_id',
        }).then(() => {
            const target = document.getElementById(el.dataset.ftScrollTarget || '');
            if (target) target.scrollIntoView({ behavior: 'smooth', block: action === 'viewer-random' ? 'nearest' : 'start', inline: 'nearest' });
        });
        return;
    }
    if (action === 'add-creature') {
        // The picker opens in the FORMS host (#div_refresh_sidelog_form — the FT3D Forms window), not the
        // Workspace, so a creature opened from its results shows its card in the Workspace BESIDE the
        // picker instead of replacing it (owner 2026-09-11). Same order as openSideForm(): bring the Forms
        // host forward, then load — but post the campaign/encounter context (openSideForm posts nothing).
        _ftTwgCall('forceOpenSideLog');
        document.getElementById('pills_sidelog_form_tab')?.click();
        void _ftTwgQueryDataInputs(el.dataset.ftUrl || '', 'div_refresh_sidelog_form', {
            campaign_id: 'campaign_id',
            encounter_id: 'encounter_id',
        }).then(() => {
            // Search-first picker: the cursor lands in its search box on open (owner 2026-09-10).
            const search = document.getElementById('input_search_creature') as HTMLInputElement | null;
            if (search) search.focus();
            else document.getElementById('div_encounter_creatures')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        return;
    }
    if (action === 'query-inputs') {
        const target = el.dataset.ftTarget || 'div_sidelog_warning';
        const payload: Record<string, unknown> = {};
        for (const field of (el.dataset.ftPayloadFields || '').split(',')) {
            const key = field.trim();
            if (key) payload[key] = key;
        }
        void _ftTwgQueryDataInputs(el.dataset.ftUrl || '', target, payload);
        return;
    }
    if (action === 'gm-target-action') {
        const context = _ftTwgCall('getSidelogContext') as Record<string, unknown> | null;
        if (!context) return;
        const start = (window as unknown as AnyWindow).startGmTargetAction;
        if (typeof start !== 'function') return;
        // Names the creature flag this action converges, so the picker can badge each pick's current
        // state and label Confirm with the direction it will actually go. Narrow HERE rather than
        // leaning on startGmTargetAction's runtime check — dataset is string|undefined, and the
        // option is a 'hidden' | 'private' | null union.
        const rawStateAxis = el.dataset.ftTargetActionState;
        const stateAxis = (rawStateAxis === 'hidden' || rawStateAxis === 'private') ? rawStateAxis : null;
        start({
            key: el.dataset.ftTargetActionKey || el.dataset.ftUrl || 'gm-target-action',
            label: el.dataset.ftTargetActionLabel || 'GM action',
            minTargets: 1,
            maxTargets: 1000,
            stateAxis,
            onConfirm: async (targetIds: string[]) => {
                if (el.dataset.ftTargetActionKey === 'toggle-targets-visibility') {
                    const toggle = (window as unknown as { ftToggleCreatureVisibility20?: (encounterId: string, ids: string[], rollPrivate: number) => Promise<unknown> }).ftToggleCreatureVisibility20;
                    if (!toggle) { displayWarning('Creature visibility is not available on this page — reload and try again.'); return; }
                    await toggle(String(context.encounter_id ?? ''), targetIds, Number(context.roll_private ?? 0) || 0);
                    return;
                }
                // Award Luck is client truth only (websocket/resource-truth.ts ftAwardLuck — the loader gets every row, one commit,
                // words on a refusal); gm_award_luck_inc.php is gone (N6 2026-10-01).
                if (el.dataset.ftTargetActionKey === 'award-luck') {
                    const award = (window as unknown as { ftAwardLuck?: (input: { targetIds: string[]; encounterId?: string; rollPrivate?: number }) => Promise<unknown> }).ftAwardLuck;
                    if (typeof award !== 'function') { displayWarning('Award Luck is not available on this page — reload and try again.'); return; }
                    await award({ targetIds, encounterId: String(context.encounter_id ?? ''), rollPrivate: Number(context.roll_private ?? 0) || 0 });
                    return;
                }
                return _ftTwgQueryData(
                    el.dataset.ftUrl || '',
                    el.dataset.ftTarget || 'div_sidelog_warning',
                    {
                        roll_private: context.roll_private ?? 0,
                        campaign_id: context.campaign_id ?? '',
                        encounter_id: context.encounter_id ?? '',
                        target_id: targetIds.join(','),
                    },
                );
            },
        });
        return;
    }
    if (action === 'query-data') {
        const payload: Record<string, unknown> = {};
        for (const pair of (el.dataset.ftPayload || '').split('|')) {
            const idx = pair.indexOf('=');
            if (idx <= 0) continue;
            payload[pair.slice(0, idx)] = pair.slice(idx + 1);
        }
        void _ftTwgQueryData(el.dataset.ftUrl || '', el.dataset.ftTarget || 'div_sidelog_warning', payload);
        return;
    }
    if (action === 'call') {
        const method = el.dataset.ftMethod || '';
        const rawArgs = el.dataset.ftArgs || '';
        const args = rawArgs === '' ? [] : rawArgs.split('|').map((arg) => {
            if (arg === 'true') return true;
            if (arg === 'false') return false;
            if (/^-?\d+(?:\.\d+)?$/.test(arg)) return Number(arg);
            return arg;
        });
        _ftTwgCall(method, ...args);
        return;
    }
    if ((action === 'long-rest-show' || action === 'long-rest-confirm')
        && (document.getElementById('input_hidden_gm_controls_encounter_in_combat') as HTMLInputElement | null)?.value === '1') return;
    if (action === 'long-rest-show') {
        const confirm = document.getElementById('btn_long_rest_party');
        const show = document.getElementById('btn_show_long_rest_party');
        if (confirm) confirm.style.display = 'inline-block';
        if (show) show.style.display = 'none';
        return;
    }
    if (action === 'long-rest-confirm') {
        _ftTwgCall('longRestParty');
        const confirm = document.getElementById('btn_long_rest_party');
        const show = document.getElementById('btn_show_long_rest_party');
        if (confirm) confirm.style.display = 'none';
        if (show) show.style.display = 'inline-block';
        return;
    }
    if (action === 'action-refresh') {
        _ftTwgCall('actionRefresh', el.dataset.ftUrl || '');
        return;
    }
    if (action === 'open-side-form') {
        _ftTwgCall('openSideForm', el.dataset.ftUrl || '');
        return;
    }
    if (action === 'view-campaign-players') {
        // Rendered into the GM tool host, which my_campaign.php shows in the FT3D Workspace for this action. The standalone
        // page is only the fallback where there is no host or catalog bundle.
        const render = (window as unknown as {
            ftRenderCampaignPlayersTool?: (host: HTMLElement, campaignId: string) => boolean;
        }).ftRenderCampaignPlayersTool;
        const host = document.getElementById('div_refresh_gm_viewer');
        if (host && typeof render === 'function' && render(host, el.dataset.ftCampaignId || '')) return;
        if (el.dataset.ftUrl) window.open(el.dataset.ftUrl, '_blank', 'noopener');
        return;
    }
    if (action === 'auto-end-turn') {
        const autoEndTurn = el.dataset.ftAutoEndTurn === '1' ? 1 : 0;
        const source = autoEndTurn ? 'auto-end-disabled' : 'auto-end-enabled';
        const setTurnTimerState = (window as unknown as AnyWindow).ftSetTurnTimerState;
        if (typeof setTurnTimerState === 'function') {
            setTurnTimerState({ auto_end_turn: autoEndTurn }, source);
        }
        _ftTwgCall('setToggleCookieAutoEndTurn');
        const on = document.getElementById('btn_auto_end_turn_on');
        const off = document.getElementById('btn_auto_end_turn_off');
        if (autoEndTurn) {
            if (on) on.style.display = 'none';
            if (off) off.style.display = '';
        } else {
            if (on) on.style.display = '';
            if (off) off.style.display = 'none';
        }
        return;
    }
    if (action === 'toggle-scene-edit') {
        const toggle = (window as unknown as AnyWindow).ftToggleFt3dEditMode;
        if (typeof toggle === 'function') {
            (toggle as () => void)();
            return;
        }
        // Shell API not published yet: the bezel button owns the state, so drive it directly.
        document.querySelector<HTMLElement>('[data-ft3d-edit-mode]')?.click();
        return;
    }
    if (action === 'set-ft3d-session-mode') {
        const mode = el.dataset.ft3dSessionMode || 'active';
        const campaignId = el.dataset.ftCampaignId || '';
        const applyMode = (window as unknown as AnyWindow).ftApplyFt3dSessionMode;
        if (typeof applyMode === 'function') applyMode(mode, 'gm-control');

        // The transport is SYMMETRIC: Resume starts campaign time, Hard Pause parks it. Theatre is
        // deliberately untouched — it is a non-blocking campaign notice and play stays interactive,
        // so the clock keeps whatever state the GM left it in. `force` because this is the GM's own
        // explicit gesture: a stale local read must never swallow it. This is the ONE place session
        // mode touches the clock — ftApplyFt3dSessionMode must not, it also runs from campaign_update.
        if (mode === 'active' || mode === 'paused') {
            ftSetCampaignClockRunning(mode === 'active', { force: true });
        }

        const rebuild = () => {
            const fateTide = (window as unknown as { FateTide?: { campaignCache?: Map<string, CampaignArr> } }).FateTide;
            const campaign = fateTide?.campaignCache?.get(campaignId);
            if (campaign && typeof applyMode === 'function') applyMode(_ftTwgVal(campaign, 'campaign_ft3d_mode') || 'active', 'gm-control-settled');
            const buildControls = (window as unknown as AnyWindow).buildTurnWidgetGMControls;
            if (typeof buildControls === 'function') {
                void Promise.resolve((buildControls as () => unknown)()).catch(() => { /* reconciles on next push */ });
            }
        };
        void _ftTwgQueryData(
            el.dataset.ftUrl || '',
            'div_sidelog_warning',
            {
                campaign_id: campaignId,
                encounter_id: el.dataset.ftEncounterId || '',
                campaign_ft3d_mode: mode,
            },
        ).then(rebuild, rebuild);
        return;
    }
    if (action === 'toggle-campaign-state') {
        const campaignId = el.dataset.ftCampaignId || '';
        const payload: Record<string, unknown> = {
            campaign_id: campaignId,
            encounter_id: el.dataset.ftEncounterId || '',
        };
        const field = el.dataset.ftField || '';
        if (field) payload[field] = el.dataset.ftValue || '';
        // campaign_update installs the persisted result (including interlocked siblings) before
        // projecting it. Do not evict that result or optimistically mutate authored DOM behind the
        // patcher's back. A failed request leaves the last received state visible; a late push still
        // converges through the ordinary campaign caller.
        const reconcileCampaignControls = () => {
            const rebuild = (window as unknown as AnyWindow).buildTurnWidgetGMControls;
            if (typeof rebuild === 'function') {
                // Best-effort: swallow a rebuild rejection so it can't surface as an unhandled rejection.
                void Promise.resolve((rebuild as () => unknown)()).catch(() => { /* display-only rebuild */ });
            }
        };
        void _ftTwgQueryData(el.dataset.ftUrl || '', 'div_sidelog_warning', payload)
            .then(reconcileCampaignControls, reconcileCampaignControls);
        return;
    }
    if (action === 'change-light') { _ftTwgCall('changeEncounterLightType'); return; }
    if (action === 'toggle-gm-visibility') { _ftTwgCall('ftToggleFt3dGmVisibility'); return; }
    if (action === 'toggle-director-spotlight') { _ftTwgCall('ftToggleFt3dDirectorSpotlight'); return; }
    if (action === 'change-weather') { _ftTwgCall('changeEncounterWeather'); return; }
    if (action === 'view-links') { _ftTwgCall('viewEncounterLinks'); return; }
    if (action === 'view-public') { _ftTwgCall('viewPublicEncounters'); return; }
    if (action === 'view-private') { _ftTwgCall('viewPrivateEncounters'); return; }
    if (action === 'view-all') { _ftTwgCall('viewAllEncounters'); }
});


function _ftTwgEsc(s: unknown): string {
    if (typeof escapeHtml === 'function') {
        return escapeHtml(s == null ? '' : String(s));
    }
    const esc: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return String(s == null ? '' : s).replace(/[&<>"']/g, c => esc[c] || c);
}

// PHP-style $arr[key][0] read with safe '' fallback.
function _ftTwgVal(arr: EncounterArr | CampaignArr | null | undefined, key: string): string {
    if (!arr) return '';
    // Source-table bucket reshapes: campaigns-table columns live in `arr.campaigns` (2026-06-04)
    // and encounters-table columns in `arr.encounters` (2026-06-09), both as bare scalars. Each
    // arr type carries only its own bucket, so checking both is a no-op for the other. Catalog-ref
    // columns inside the encounters bucket are shelled {items:{item_id,...}} — unwrap to the UUID
    // (mirrors encounterField in shared/array-types).
    for (const bucketKey of ['campaigns', 'encounters'] as const) {
        const bucket = (arr as { [k: string]: unknown })[bucketKey];
        if (bucket && typeof bucket === 'object' && key in (bucket as object)) {
            let bv = (bucket as { [k: string]: unknown })[key];
            if (bv && typeof bv === 'object' && typeof (bv as { items?: { item_id?: unknown } }).items?.item_id === 'string') {
                bv = (bv as { items: { item_id: string } }).items.item_id;
            }
            return bv == null ? '' : String(bv);
        }
    }
    const wrapped = arr[key];
    if (Array.isArray(wrapped)) {
        return wrapped.length === 0 || wrapped[0] == null ? '' : String(wrapped[0]);
    }
    // Bare descalared top-level scalar fallback.
    return wrapped == null ? '' : String(wrapped);
}


// Resolve the encounter's primary picture URL for inline thumbnail display.
// Prefers the encounter's own uploaded picture (no `reference_attached_item_type`)
// over location-inherited reference rows — when an encounter has both its own
// upload AND a location_picture reference attached, the own upload should win.
// fetch_pictures already orders by primary_picture DESC so [0] is the GM's chosen
// primary; the reference filter is the secondary tiebreak.
function _ftTwgFirstEncounterPicUrl(enc: EncounterArr | null | undefined): string {
    if (!enc) return '';
    // Bucket-aware read: pictures live under enc.pictures.encounter_picture (2026-05-28
    // pictures-bucket reshape); picturesOf falls back to the legacy top-level slot.
    const pics = picturesOf(enc, 'encounter_picture') as PictureRow[];
    if (!Array.isArray(pics) || pics.length === 0) return '';
    // Column renamed reference_attached_item_type -> picture_reference_attached_item_type
    // (2026-06-02). Tolerate the legacy key for un-rolled cached blobs during warm-up.
    const ownUpload = pics.find(p => p && !(p.picture_reference_attached_item_type ?? p.reference_attached_item_type));
    const pic = ownUpload || pics[0];
    const url = pic ? pic.picture_url : undefined;
    return typeof url === 'string' ? url : '';
}


////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Hidden inputs (lines 28-42 of turn_widget_gm_controls_inc.php pre-refactor).
// input_hidden_gm_controls_encounter_in_combat is intentionally NOT here — it lives in Row 3
// alongside the combat start/stop block.
////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export function ftTwgRenderHiddenInputs(enc: EncounterArr): string {
    const eid  = _ftTwgEsc(_ftTwgVal(enc, 'encounter_id'));
    const ccid = _ftTwgEsc(_ftTwgVal(enc, 'current_encounter_creature_id_turn'));
    return `
<!-- HIDDEN INPUT FOR GM TURN CONTROLS REFRESH -->
<input id="input_hidden_gm_controls_encounter_id" type="hidden" value="${eid}">

<!-- HIDDEN INPUT FOR GM LOG REFRESH -->
<input id="input_hidden_gm_controls_current_encounter_creature_id_turn" type="hidden" value="${ccid}">
`;
}


////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Compact campaign-page combat overview.
//
// This is a
// deliberately read-only mirror for the top command area: it makes combat state, round, current turn,
// and roster legible before the GM scrolls to the map without duplicating draggable ids or controls.
// Visibility follows the browser predicate, and dead rows stay excluded.
////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Row 1 — Encounter actions (lines 44-184 of turn_widget_gm_controls_inc.php pre-refactor).
//
// Inline queryDataInputs payloads pass element-id mappings so sidelog hidden inputs can be removed.
// getElementValue() resolves campaign/encounter ids through FateTide context helpers.
////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export function ftTwgRenderRow1EncounterActions(enc: EncounterArr, cam: CampaignArr | null): string {
    const encounterId        = _ftTwgVal(enc, 'encounter_id');
    const eid                = _ftTwgEsc(_ftTwgVal(enc, 'encounter_id'));
    const inventoryUrl       = BASE_URL + 'options/tools/inventory/encounter_inventory_inc.php';
    const randomUrl          = BASE_URL + 'options/tools/inventory/encounter_random_generators_inc.php';
    const addCreatureUrl     = BASE_URL + 'options/tools/encounter_creatures/forms/form_add_encounter_creatures_inc.php';
    const encDesc            = _ftTwgVal(enc, 'encounter_description');
    const encGmNotes         = _ftTwgVal(enc, 'encounter_gm_notes');
    const campaignId         = _ftTwgEsc(_ftTwgVal(cam, 'campaign_id'));
    const sessionMode        = _ftTwgVal(cam, 'campaign_ft3d_mode') || 'active';
    const sessionModeUrl     = BASE_URL + 'options/tools/campaigns/set_campaign_ft3d_mode_inc.php';
    const sessionModeButton = (mode: 'active' | 'paused' | 'theatre', icon: string, title: string): string => {
        const selected = sessionMode === mode;
        const escapedTitle = _ftTwgEsc(title);
        return `<button
            type="button"
            class="btn btn-sm btn-outline-light${selected ? ' is-active' : ''}"
            data-ft-turn-widget-action="set-ft3d-session-mode"
            data-ft3d-session-mode-control
            data-ft3d-session-mode="${mode}"
            data-ft-url="${sessionModeUrl}"
            data-ft-campaign-id="${campaignId}"
            data-ft-encounter-id="${eid}"
            aria-pressed="${selected ? 'true' : 'false'}"
            aria-label="${escapedTitle}"
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            data-bs-animation="false"
            data-bs-original-title="${escapedTitle}"
        >${iconMap(icon, 22, 22, false)}</button>`;
    };
    const sessionModeControls = document.querySelector('[data-ft3d-session-mode]') ? `
        <div id="ft-twg-encounter-1" class="p-1">
            <div class="btn-group btn-group-sm ft3d-session-mode-controls" role="group" aria-label="Player session mode">
                ${sessionModeButton('active', 'play', 'Resume normal FT3D play')}
                ${sessionModeButton('paused', 'pause', 'Pause all player interaction and campaign time')}
                ${sessionModeButton('theatre', 'roleplay', 'Enter Theatre of the Mind as a non-blocking campaign notice')}
            </div>
        </div>` : '';
    _ftTwgEncounterContent = {
        encounter_id: encounterId,
        encounter_description: encDesc,
        encounter_gm_notes: encGmNotes,
    };
    const descBtn = encDesc ? `
        <div id="ft-twg-encounter-2" class="p-1">
            <button
                id="btn_show_encounter_description"
                class="btn btn-primary btn-sm"
                data-ft-turn-widget-action="show-encounter-content"
                data-ft-target="div_refresh_gm_viewer"
                data-ft-encounter-id="${eid}"
                data-ft-encounter-content-field="encounter_description"
            >${iconMap('show_encounter_description')}</button>
        </div>
` : '';
    const gmNotesBtn = encGmNotes ? `
        <div id="ft-twg-encounter-3" class="p-1">
            <button
                id="btn_show_encounter_gm_notes"
                class="btn btn-primary btn-sm"
                data-ft-turn-widget-action="show-encounter-content"
                data-ft-target="div_refresh_gm_viewer"
                data-ft-encounter-id="${eid}"
                data-ft-encounter-content-field="encounter_gm_notes"
            >${iconMap('show_encounter_gm_notes')}</button>
        </div>
` : '';
    const contentDivider = encDesc || encGmNotes
        ? '<hr class="border border-light" style="width:1px;height:20px;display:inline-block;">'
        : '';

    return `
<!-- FIRST ROW | ENCOUNTER -->
<div class="d-flex justify-content-start align-items-center mb-1" data-ft-gm-control-group="encounter">

    <!-- music console (theme picker: Everyone / Just me / back to default) -->
    <!-- music-console.ts owns the delegated document listener. -->
    <div id="ft-twg-encounter-4" class="p-1">
        <button
            type="button"
            class="btn btn-sm btn-primary"
            data-ft-music-action="open-form"
            title="Music — pick a theme, or leave it on auto"
            aria-label="Choose music theme"
        >${iconMap('music')}</button>
    </div>


    <!-- encounter_description -->${descBtn}

    <!-- encounter_gm_notes -->${gmNotesBtn}
${contentDivider}


    <!-- DIVIDER -->
    <hr class="border border-light" style="width: 1px; height: 20px; display: inline-block;">


    <!-- encounter inventory & random generators → GM viewer -->
    <div id="ft-twg-encounter-5" class="p-1 d-flex flex-wrap align-items-stretch">
        <div
            class="btn-group btn-group-sm"
            role="group"
            aria-label="Encounter inventory tools"
            id="turn_widget_gm_inventory_random_group"
        >
            <button
                type="button"
                class="btn btn-sm btn-primary"
                id="turn_widget_btn_gm_viewer_inventory"
                data-ft-turn-widget-action="viewer-inventory"
                data-ft-url="${inventoryUrl}"
                data-ft-scroll-target="div_encounter_inventory"
            >${iconMap('inventory', 22, 22, true)}</button>
            <button
                type="button"
                class="btn btn-sm btn-outline-primary"
                id="turn_widget_btn_gm_viewer_random"
                data-ft-turn-widget-action="viewer-random"
                data-ft-url="${randomUrl}"
                data-ft-scroll-target="div_encounter_random_generators"
            >${iconMap('random', 22, 22, true)}</button>
        </div>
    </div>


    <!-- add_creature -->
    <div id="ft-twg-encounter-6" class="p-1">
        <button
            type="button"
            class="btn btn-sm btn-danger"
            data-ft-turn-widget-action="add-creature"
            data-ft-url="${addCreatureUrl}"
        >${iconMap('add_creatures')}</button>
    </div>


    <!-- Viewer-local GM darkness inspection. Never changes encounter state or player vision.
         The tooltip lives on the BUTTON, not on the icon — hence iconMap(..., false), which returns a
         bare <img> instead of its own .ft-icon-map tooltip span. template.js disposes .ft-icon-map
         tooltips on pointerdown/focusin inside a clickable host, so an icon-level label would be torn
         down by the very click that flips the state we are trying to report.
         The On/Off label is OWNED BY reflectFt3dGmVisibilityControls() in my_campaign.php because it
         is state-dependent; the Off text below is only the pre-reflect fallback. No "title" attribute:
         BS 5.3.8 resolves _getTitle() as config.title || data-bs-original-title, so the attribute
         alone drives template.js's hover delegation, and adding "title" would stack a native browser
         tooltip on top of the Bootstrap one. aria-label stays STATIC — aria-pressed already carries
         the state, and a name that flips to "GM Vision On" would read as "GM Vision On, pressed". -->
    <div id="ft-twg-encounter-7" class="p-1">
        <button
            type="button"
            class="btn btn-sm btn-outline-info"
            data-ft-turn-widget-action="toggle-gm-visibility"
            data-ft3d-gm-visibility-toggle
            aria-pressed="false"
            aria-label="GM Vision"
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            data-bs-original-title="GM Vision Off"
        >${iconMap('gm_vision', 25, 25, false)}</button>
    </div>

    <!-- Encounter-wide GM presentation cue. It never changes rules-bearing light or perception. -->
    <div id="ft-twg-encounter-8" class="p-1">
        <button
            type="button"
            class="btn btn-sm btn-outline-warning d-inline-flex align-items-center justify-content-center"
            data-ft-turn-widget-action="toggle-director-spotlight"
            data-ft3d-director-spotlight-toggle
            aria-pressed="false"
            aria-label="Director Spotlight"
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            data-bs-original-title="Director Spotlight Off — visible to everyone (L)"
        ><img src="https://cdn.fatetide.com/images/spotlight.png" width="25" height="25" alt="" aria-hidden="true" class="d-block"></button>
    </div>

    ${sessionModeControls}

</div>
`;
}


////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Row 2 — Encounter navigation + Campaign state toggles (lines 36-344 of the PHP partial pre-Phase-2).
//
// Two interlocks preserved verbatim from PHP:
//   • Haven-return DISABLE button is `disabled` when campaign_downtime_enabled == 1.
//   • Downtime ENABLE button is `disabled` when campaign_disable_player_haven_return == 1.
//
// Location map button shows iff the encounter's own location has a map; falls back to the
// region parent's map if the encounter has no usable location_id.
////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export function ftTwgRenderRow2EncounterNavCampaignState(enc: EncounterArr, cam: CampaignArr | null): string {
    const eid = _ftTwgEsc(_ftTwgVal(enc, 'encounter_id'));
    const cid = _ftTwgEsc(_ftTwgVal(cam, 'campaign_id'));

    // --- location map fallback (encounter location first, else region parent) ---
    let mapLocationId: string | null = null;
    const encLocId    = _ftTwgVal(enc, 'encounter_location_id');
    const encLocMap   = _ftTwgVal(enc, 'encounter_location_map');
    const parentLocId = _ftTwgVal(enc, 'encounter_location_parent_id');
    const parentMap   = _ftTwgVal(enc, 'location_parent_map');
    if (encLocId && checkIfValidUUID(encLocId) && encLocMap) {
        mapLocationId = encLocId;
    } else if (parentLocId && checkIfValidUUID(parentLocId) && parentMap) {
        mapLocationId = parentLocId;
    }
    const mapBtn = mapLocationId
        ? `
        <div id="ft-twg-navigation-1" class="p-1">
            <a
                href="${BASE_URL}tools/locations/view_map.php?location_id=${_ftTwgEsc(mapLocationId)}"
                target="_blank"
                class="btn btn-sm btn-warning"
            >
                ${iconMap('map')}
            </a>
        </div>`
        : '';

    // --- encounter pictures: the inline thumb IS the gallery link, so no separate
    // icon button is rendered. When the encounter has no picture yet, nothing shows
    // here at all (the encounter list / modify_encounter page is the upload path). ---
    const encPicUrl = _ftTwgFirstEncounterPicUrl(enc);
    // Background-image div instead of <img class="img-thumbnail"> — Bootstrap's
    // .img-thumbnail applies `height: auto` which (despite inline precedence) was
    // conflicting with the 38px height in some layouts and collapsing the thumbnail
    // to a thin vertical sliver. A bordered div with background-image:cover is fully
    // self-contained and renders the same way regardless of the loaded image's aspect.
    // Width/height tuned to match btn-sm + iconMap(25,25) — Bootstrap btn-sm has
    // ~0.25rem 0.5rem padding so the rendered button is ~33px tall. Matching that
    // keeps the thumb visually flush with the rest of the row instead of bulging
    // above/below. vertical-align:middle on the inline-block link locks centering
    // inside the flex item regardless of the surrounding row's baseline.
    const encPicThumb = encPicUrl
        ? `
        <div id="ft-twg-navigation-2" class="p-1 d-flex align-items-center">
            <a
                href="${BASE_URL}tools/encounters/encounter_pictures.php?encounter_id=${eid}"
                target="_blank"
                title="View encounter pictures"
                style="display:inline-block;width:40px;height:40px;vertical-align:middle;background-image:url('${_ftTwgEsc(encPicUrl)}');background-size:cover;background-position:center;border:1px solid #555;border-radius:4px;"
            ></a>
        </div>`
        : '';

    // --- interlock state for haven-return / downtime toggles ---
    const havenReturnEnabledState =
        parseInt(_ftTwgVal(cam, 'campaign_disable_player_haven_return') || '0', 10) !== 1;
    const downtimeEnabledState =
        parseInt(_ftTwgVal(cam, 'campaign_downtime_enabled') || '0', 10) === 1;
    const havenReturnCannotDisable     = downtimeEnabledState;
    const downtimeEnableBlockedByHaven = !havenReturnEnabledState;

    const havenReturnEnabledStyle  = havenReturnEnabledState ? 'inline-block' : 'none';
    const havenReturnDisabledStyle = havenReturnEnabledState ? 'none' : 'inline-block';
    const downtimeEnabledStyle     = downtimeEnabledState ? 'inline-block' : 'none';
    const downtimeDisabledStyle    = downtimeEnabledState ? 'none' : 'inline-block';

    // Interlock reasons. A `disabled` <button> does NOT dispatch mouse events, so the site's
    // delegated mouseover tooltip system (template.js) can never fire a tooltip attached to the
    // button itself. Per the Bootstrap docs, wrap the disabled control in a focusable <span> that
    // carries the tooltip and set pointer-events:none on the button so hover passes through to the
    // wrapper. When the button is NOT disabled the wrapper collapses to nothing and the button's own
    // iconMap() tooltip works as usual.
    const havenDisableReason   = 'Cannot disable haven return while campaign downtime is enabled.';
    const downtimeEnableReason = 'Haven return must be enabled before campaign downtime can be enabled.';

    const havenDisableAttr = havenReturnCannotDisable
        ? `disabled aria-label="${_ftTwgEsc(havenDisableReason)}"`
        : '';
    const downtimeEnableAttr = downtimeEnableBlockedByHaven
        ? `disabled aria-label="${_ftTwgEsc(downtimeEnableReason)}"`
        : '';

    // pointer-events:none lets the wrapper span receive the hover that the disabled button swallows.
    const havenDisableBtnStyle   = havenReturnCannotDisable ? ' pointer-events: none;' : '';
    const downtimeEnableBtnStyle = downtimeEnableBlockedByHaven ? ' pointer-events: none;' : '';

    const ftTwgDisabledTipWrapOpen = (reason: string, disabled: boolean): string =>
        `<span class="d-inline-block"${disabled ? ` tabindex="0" style="cursor: not-allowed;" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-original-title="${_ftTwgEsc(reason)}" aria-label="${_ftTwgEsc(reason)}"` : ''}>`;

    const havenDisableWrapOpen    = ftTwgDisabledTipWrapOpen(havenDisableReason, havenReturnCannotDisable);
    const havenDisableWrapClose   = '</span>';
    const downtimeEnableWrapOpen  = ftTwgDisabledTipWrapOpen(downtimeEnableReason, downtimeEnableBlockedByHaven);
    const downtimeEnableWrapClose = '</span>';

    // Push-to-stronghold has nowhere to go until a stronghold is set; same disabled-tooltip wrapper.
    // campaign-update.ts rebuilds the controls when campaign_stronghold_encounter_id moves.
    const hasStronghold          = checkIfValidUUID(_ftTwgVal(cam, 'campaign_stronghold_encounter_id'));
    const pushStrongholdReason   = 'No stronghold set. Use Set Campaign Stronghold first.';
    const pushStrongholdAttr     = hasStronghold ? '' : `disabled aria-label="${_ftTwgEsc(pushStrongholdReason)}"`;
    const pushStrongholdBtnStyle = hasStronghold ? '' : 'pointer-events: none;';
    const pushStrongholdWrapOpen  = ftTwgDisabledTipWrapOpen(pushStrongholdReason, !hasStronghold);
    const pushStrongholdWrapClose = '</span>';

    const pushCampsiteUrl    = BASE_URL + 'options/tools/encounters/gm_load_encounter/gm_push_party_to_campaign_campsite_inc.php';
    const pushStrongholdUrl  = BASE_URL + 'options/tools/encounters/gm_load_encounter/gm_push_party_to_campaign_stronghold_inc.php';
    const setCampsiteUrl     = BASE_URL + 'options/tools/campaigns/set_campaign_campsite_inc.php';
    const setStrongholdUrl   = BASE_URL + 'options/tools/campaigns/set_campaign_stronghold_inc.php';
    const havenToggleUrl     = BASE_URL + 'options/tools/campaigns/toggle_campaign_disable_player_haven_return_inc.php';
    const downtimeToggleUrl  = BASE_URL + 'options/tools/campaigns/toggle_campaign_downtime_enabled_inc.php';
    const partyQueryUrl      = BASE_URL + 'options/tools/encounters/gm_load_encounter/form_query_campaign_player_characters.php';

    return `
<!-- SECOND ROW | ENCOUNTERS -->
<div class="d-flex justify-content-start align-items-center mb-1" data-ft-gm-control-group="campaign">


    <!-- location map: encounter's location first, else parent region map -->${mapBtn}


    <!-- encounter_pictures -->${encPicThumb}


    <!-- The five encounter link/browse buttons that used to sit here moved to their own
         links tab on 2026-09-02 (ftTwgRenderRowLinks) — the campaign row had grown past
         the GM window's width. -->


    <!-- party_vote — ask the table a yes/no question. Ephemeral: the poll is a Redis sidecar on
         the campaign channel, and only its verdict ever reaches the log. -->
    <div id="ft-twg-navigation-3" class="p-1">
        <button
            class="btn btn-sm btn-warning"
            type="button"
            data-ft-turn-widget-action="call"
            data-ft-method="ftFt3dOpenPartyVote"
        >
            ${iconMap('party_vote')}
        </button>
    </div>


    <!-- go_to_party_member -->
    <div id="ft-twg-navigation-4" class="p-1">
        <button
            type="button"
            class="btn btn-sm btn-warning"
            data-ft-turn-widget-action="query-inputs"
            data-ft-url="${partyQueryUrl}"
            data-ft-target="div_refresh_gm_viewer"
            data-ft-payload-fields="campaign_id"
        >
            ${iconMap('go_to_player_character')}
        </button>
    </div>


    <!-- Bordered clusters (owner 2026-10-26): A = haven return + downtime toggles, B = campsite,
         C = stronghold. Each destination's push sits beside its set button, so the two near-identical
         push icons read by the tent / keep next to them. -->

    <!-- CLUSTER A | haven return + downtime -->
    <div class="ft-twg-gm-cluster" role="group" aria-label="Haven return and downtime">

    <!-- campaign_disable_player_haven_return (character sheet return to haven) -->
    <div id="ft-twg-navigation-5" class="p-1">
        ${havenDisableWrapOpen}<button
            id="haven_return_enabled_btn"
            class="btn btn-sm btn-light border"
            style="display: ${havenReturnEnabledStyle};${havenDisableBtnStyle}"
            ${havenDisableAttr}
            data-ft-turn-widget-action="toggle-campaign-state"
            data-ft-url="${havenToggleUrl}"
            data-ft-campaign-id="${cid}"
            data-ft-encounter-id="${eid}"
            data-ft-field="campaign_disable_player_haven_return"
            data-ft-value="1"
        >${iconMap('haven_return_enabled')}</button>${havenDisableWrapClose}

        <button
            id="haven_return_disabled_btn"
            class="btn btn-sm btn-dark border"
            style="display: ${havenReturnDisabledStyle};"
            data-ft-turn-widget-action="toggle-campaign-state"
            data-ft-url="${havenToggleUrl}"
            data-ft-campaign-id="${cid}"
            data-ft-encounter-id="${eid}"
            data-ft-field="campaign_disable_player_haven_return"
            data-ft-value="0"
        >${iconMap('haven_return_disabled')}</button>
    </div>


    <!-- campaign_downtime_enabled -->
    <div id="ft-twg-navigation-6" class="p-1">
        <button
            id="campaign_downtime_enabled_btn"
            class="btn btn-sm btn-light border"
            style="display: ${downtimeEnabledStyle};"
            data-ft-turn-widget-action="toggle-campaign-state"
            data-ft-url="${downtimeToggleUrl}"
            data-ft-campaign-id="${cid}"
            data-ft-encounter-id="${eid}"
            data-ft-field="campaign_downtime_enabled"
            data-ft-value="0"
        >${iconMap('campaign_downtime_enabled')}</button>

        ${downtimeEnableWrapOpen}<button
            id="campaign_downtime_disabled_btn"
            class="btn btn-sm btn-dark border"
            style="display: ${downtimeDisabledStyle};${downtimeEnableBtnStyle}"
            ${downtimeEnableAttr}
            data-ft-turn-widget-action="toggle-campaign-state"
            data-ft-url="${downtimeToggleUrl}"
            data-ft-campaign-id="${cid}"
            data-ft-encounter-id="${eid}"
            data-ft-field="campaign_downtime_enabled"
            data-ft-value="1"
        >${iconMap('campaign_downtime_disabled')}</button>${downtimeEnableWrapClose}
    </div>

    </div>


    <!-- CLUSTER B | campsite -->
    <div class="ft-twg-gm-cluster" role="group" aria-label="Campsite">

    <!-- push_party_to_campaign_campsite -->
    <div id="ft-twg-navigation-7" class="p-1">
        <button
            type="button"
            class="btn btn-sm btn-primary"
            data-ft-turn-widget-action="query-inputs"
            data-ft-url="${pushCampsiteUrl}"
            data-ft-target="div_sidelog_warning"
            data-ft-payload-fields="campaign_id,encounter_id"
        >
            ${iconMap('push_party_to_campaign_campsite')}
        </button>
    </div>


    <!-- set_campaign_campsite -->
    <div id="ft-twg-navigation-8" class="p-1">
        <button
            type="button"
            class="btn btn-sm btn-secondary"
            data-ft-turn-widget-action="query-inputs"
            data-ft-url="${setCampsiteUrl}"
            data-ft-target="div_sidelog_warning"
            data-ft-payload-fields="campaign_id,encounter_id"
        >
            ${iconMap('set_campaign_campsite')}
        </button>
    </div>

    </div>


    <!-- CLUSTER C | stronghold -->
    <div class="ft-twg-gm-cluster" role="group" aria-label="Stronghold">

    <!-- push_party_to_campaign_stronghold — disabled until the campaign has a stronghold -->
    <div id="ft-twg-navigation-9" class="p-1">
        ${pushStrongholdWrapOpen}<button
            type="button"
            class="btn btn-sm btn-primary"
            style="${pushStrongholdBtnStyle}"
            ${pushStrongholdAttr}
            data-ft-turn-widget-action="query-inputs"
            data-ft-url="${pushStrongholdUrl}"
            data-ft-target="div_sidelog_warning"
            data-ft-payload-fields="campaign_id,encounter_id"
        >
            ${iconMap('push_party_to_campaign_stronghold')}
        </button>${pushStrongholdWrapClose}
    </div>


    <!-- set_campaign_stronghold -->
    <div id="ft-twg-navigation-10" class="p-1">
        <button
            type="button"
            class="btn btn-sm btn-secondary"
            data-ft-turn-widget-action="query-inputs"
            data-ft-url="${setStrongholdUrl}"
            data-ft-target="div_sidelog_warning"
            data-ft-payload-fields="campaign_id,encounter_id"
        >
            ${iconMap('set_campaign_stronghold')}
        </button>
    </div>

    </div>


</div>
`;
}


////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Links row — the encounter browse/jump controls, split out of the campaign row on 2026-09-02.
// Four delegated `view-*` actions (links / public / private / all) plus the external my_encounters
// page. Rendered as its own `links` control group so the GM window's Links tab can show it.
////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export function ftTwgRenderRowLinks(_enc: EncounterArr, cam: CampaignArr | null): string {
    const cid = _ftTwgEsc(_ftTwgVal(cam, 'campaign_id'));
    const myEncountersUrl = BASE_URL + 'tools/encounters/my_encounters.php?campaign_id=' + cid;

    return `
<!-- LINKS ROW -->
<div class="d-flex justify-content-start align-items-center mb-1" data-ft-gm-control-group="links">


    <!-- encounter_links -->
    <div class="p-1">
        <button
            class="btn btn-sm btn-warning"
            type="button"
            data-ft-turn-widget-action="view-links"
        >
            ${iconMap('encounter_links')}
        </button>
    </div>


    <!-- view_public_encounters -->
    <div class="p-1">
        <button
            class="btn btn-sm btn-warning"
            type="button"
            data-ft-turn-widget-action="view-public"
        >
            ${iconMap('view_public_encounters')}
        </button>
    </div>


    <!-- view_private_encounters -->
    <div class="p-1">
        <button
            class="btn btn-sm btn-warning"
            type="button"
            data-ft-turn-widget-action="view-private"
        >
            ${iconMap('view_private_encounters')}
        </button>
    </div>


    <!-- view_all_campaign_encounters -->
    <div class="p-1">
        <button
            class="btn btn-sm btn-warning"
            type="button"
            data-ft-turn-widget-action="view-all"
        >
            ${iconMap('view_all_encounters')}
        </button>
    </div>


    <!-- view_encounters -->
    <div class="p-1">
        <a
            href="${myEncountersUrl}"
            target="_blank"
            class="btn btn-sm btn-dark border"
        >${iconMap('view_encounters')}</a>
    </div>


</div>
`;
}


////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Edit-encounter row — the "change the encounter itself" controls, split out of the encounter row
// on 2026-09-09 (owner: the encounter row had grown past one line and wrapped to two). Everything here MUTATES the
// encounter's own setup rather than running the table: its light, its weather, its layout, and the
// creation of a new one. What stays on the encounter row is what a GM touches DURING play (music,
// description/notes, inventory, add creature, GM vision, spotlight, session mode).
////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export function ftTwgRenderRowEditEncounter(enc: EncounterArr, cam: CampaignArr | null): string {
    const eid                = _ftTwgEsc(_ftTwgVal(enc, 'encounter_id'));
    const modifyEncounterUrl = BASE_URL + 'encounter.php?scene=edit&encounter_id=' + eid;
    // The bezel button is server-rendered for a campaign manager, so it is a render-order-proof
    // signal that this route has an in-place Edit Scene; the shell's globals may not exist yet
    // when this row first renders (they are published in the shell's DOMContentLoaded work).
    const createEncounterFormUrl = BASE_URL + 'options/tools/encounters/forms/form_create_encounter_inc.php?encounter_master_parent_id=' + encodeURIComponent(_ftTwgVal(cam, 'campaign_id'));
    const editSceneBezel = document.querySelector<HTMLElement>('[data-ft3d-edit-mode]');
    const editSceneRead = (window as unknown as AnyWindow).ftIsFt3dEditMode;
    const editSceneAvailable = !!editSceneBezel;
    const editSceneActive = editSceneAvailable && (
        typeof editSceneRead === 'function'
            ? (editSceneRead as () => boolean)() === true
            : editSceneBezel.getAttribute('aria-pressed') === 'true'
    );
    const editSceneControl = editSceneAvailable
        ? `<button
            type="button"
            class="btn btn-sm btn-dark border${editSceneActive ? ' is-active' : ''}"
            data-ft-turn-widget-action="toggle-scene-edit"
            data-ft3d-edit-mode-control
            aria-pressed="${editSceneActive ? 'true' : 'false'}"
            aria-label="Edit encounter"
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            data-bs-animation="false"
            data-bs-original-title="Edit encounter in place"
        >${iconMap('modify_encounter', 25, 25, false)}</button>`
        : `<a
            href="${modifyEncounterUrl}"
            target="_blank"
            class="btn btn-sm btn-dark border"
            aria-label="Edit encounter"
        >${iconMap('modify_encounter')}</a>`;

    return `
<!-- EDIT ENCOUNTER ROW -->
<div class="d-flex justify-content-start align-items-center mb-1" data-ft-gm-control-group="edit_encounter">

    <!-- change_encounter_light_type -->
    <div class="p-1">
        <button
            type="button"
            class="btn btn-sm btn-warning"
            data-ft-turn-widget-action="change-light"
        >${iconMap('change_encounter_light_type')}</button>
    </div>

    <!-- change_encounter_weather -->
    <div class="p-1">
        <button
            type="button"
            class="btn btn-sm btn-warning"
            data-ft-turn-widget-action="change-weather"
        >${iconMap('change_encounter_weather')}</button>
    </div>


    <!-- DIVIDER -->
    <hr class="border border-light" style="width: 1px; height: 20px; display: inline-block;">


    <!-- Edit encounter. Inside the campaign FT3D shell this enters the SAME in-place Edit Scene
         mode as the bezel button (one state, owned by my_campaign.php). Only a route with no shell
         (encounter.php, legacy) still links out to the full-page form, which is being phased out.
         The in-shell button and the New-encounter button own their tooltip (data-bs-original-title),
         so their icons render bare — iconMap(..., false) — otherwise the icon's own .ft-icon-map
         tooltip stacks on top of the button's (the overlay clash fixed 2026-09-09; same rule as
         GM Vision above). The legacy <a> has no button tooltip, so its icon keeps its own. -->
    <div class="p-1">
        ${editSceneControl}
    </div>
    ${editSceneAvailable ? `
    <!-- New encounter: the pre-flight form (name / location / environment / weather) opens in the
         Forms window; on create the shell loads the encounter here and opens Edit Scene. Replaces the
         full-page "Create Encounter". Handlers: encounter-admin.ts (create-encounter-*). -->
    <div class="p-1">
        <button
            type="button"
            class="btn btn-sm btn-dark border"
            data-ft-turn-widget-action="open-side-form"
            data-ft-url="${createEncounterFormUrl}"
            aria-label="New encounter"
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            data-bs-animation="false"
            data-bs-original-title="New encounter (opens the create form; Edit Scene follows)"
        >${iconMap('create_encounter', 25, 25, false)}</button>
    </div>` : ''}

</div>
`;
}


////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Admin row — staff debug download of the encounter_arr already held by this page.
//
// The rank is computed locally from the same Auth\UserRole::HIERARCHY order campaign-permissions.ts
// mirrors, instead of calling window.ftUserPrimaryRoleRank: that helper lives in a different bundle
// and this one must render correctly whether or not it happens to be loaded on the host page.
// The row is a UI convenience; every viewer already holds the complete encounter state.
////////////////////////////////////////////////////////////////////////////////////////////////////////////////
const _FT_TWG_ROLE_HIERARCHY = [
    'banned', 'unverified', 'user', 'creator', 'publisher', 'employee', 'admin', 'super_admin',
];

function _ftTwgViewerIsStaff(): boolean {
    const fateTide = (window as unknown as AnyWindow).FateTide as { user?: { primaryRole?: unknown } } | undefined;
    const role = String(fateTide?.user?.primaryRole ?? '').trim();
    const rank = _FT_TWG_ROLE_HIERARCHY.indexOf(role);
    return rank >= _FT_TWG_ROLE_HIERARCHY.indexOf('employee');
}

export function ftTwgRenderRowAdmin(enc: EncounterArr, _cam: CampaignArr | null): string {
    if (!_ftTwgViewerIsStaff()) return '';

    const encounterId = _ftTwgVal(enc, 'encounter_id');
    if (!encounterId) return '';

    return `
<!-- ADMIN ROW (staff only) -->
<div class="d-flex justify-content-start align-items-center mb-1" data-ft-gm-control-group="admin">


    <div class="p-1">
        <button
            type="button"
            data-ft-turn-widget-action="download-encounter-array"
            data-ft-encounter-id="${_ftTwgEsc(encounterId)}"
            class="btn btn-sm btn-dark border"
            title="Download encounter array"
            aria-label="Download encounter array"
        >${iconMap('array_check')} <span data-ft-array-download-status role="status" aria-live="polite">Download encounter array</span></button>
    </div>


</div>
`;
}


////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Row 3 — Combat start/stop + creature-visibility controls.
// Hosts input_hidden_gm_controls_encounter_in_combat (read by code outside this module to detect
// in-combat state without re-parsing the encounter_arr).
//
// in_combat gate:
//   • 0 → initiative picker (starter, automatic participants, GM additions/exclusions).
//   • 1 → exit-combat button.
// in_combat is elided from the encounters bucket when 0 (blank-field elision,
// build_encounter_arr.php) — absence means out of combat, so default '' → '0'.
////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export function ftTwgRenderRow3CombatVisibility(enc: EncounterArr, _cam: CampaignArr | null): string {
    const inCombat = String(_ftTwgVal(enc, 'in_combat') || '0');

    let combatBlock = '';
    if (inCombat === '0') {
        combatBlock = `
            <div id="ft-twg-combat-1" class="p-1">
                <button
                    id="btn_roll_initiative"
                    class="btn btn-sm btn-danger"
                    data-ft-turn-widget-action="call"
                    data-ft-method="rollInitiative"
                    title="Roll initiative: choose who starts the fight, then review who joins. Use Add creatures to include others before starting combat."
                >${iconMap('roll_initiative')}</button>
            </div>
`;
    } else if (inCombat === '1') {
        combatBlock = `
            <div id="ft-twg-combat-2" class="p-1">
                <button
                    id="btn_exit_combat"
                    class="btn btn-sm btn-warning"
                    data-ft-turn-widget-action="call"
                    data-ft-method="exitCombat"
                >${iconMap('exit_combat')}</button>
            </div>
            <div id="ft-twg-combat-3" class="p-1">
                <button id="btn_add_to_combat" type="button" class="btn btn-sm btn-danger"
                    data-ft-turn-widget-action="call" data-ft-method="addToCombat"
                    aria-label="Add Creature to Combat"
                    title="Add creatures to combat without rerolling existing initiative or changing the current turn.">${iconMap('add_creature_to_combat')}</button>
            </div>
`;
    }

    // The two creature-visibility controls are SELECTION-SCOPED converging toggles: arm, click the
    // tokens, Confirm. The four encounter-wide blast endpoints they replaced (hide_all / reveal_all /
    // private_all / public_all) were retired 2026-09-02 — once encounters are grouped/merged, "all"
    // spans rooms the party is nowhere near, and the reveal/public half stripped concealment the GM
    // had deliberately staged. `hidden` (a status) and `private` (a column that removes the actor
    // from the engine's target set) stay TWO buttons; do not collapse them into one.
    const togglePrivateUrl    = BASE_URL + 'options/tools/encounter_creatures/gm_actions/toggle_targets_private_inc.php';

    return `
<!-- THIRD ROW -->
<div class="d-flex justify-content-start align-items-center mb-1" data-ft-gm-control-group="combat">

    <!-- START/STOP COMBAT -->

        <input
            id="input_hidden_gm_controls_encounter_in_combat"
            type="hidden"
            value="${_ftTwgEsc(inCombat)}"
        >
${combatBlock}

    <!-- DIVIDER -->
    <hr class="border border-light" style="width: 1px; height: 20px; display: inline-block;">


    <!-- toggle_targets_visibility — converging HIDDEN toggle over the picked creatures -->
    <div id="ft-twg-combat-4" class="p-1">
        <button
            id="btn_toggle_targets_visibility"
            class="btn btn-sm btn-danger"
            data-ft-turn-widget-action="gm-target-action"
            data-ft-target="div_sidelog_warning"
            data-ft-target-action-key="toggle-targets-visibility"
            data-ft-target-action-label="Toggle target visibility"
            data-ft-target-action-state="hidden"
        >${iconMap('toggle_targets_visibility')}</button>
    </div>


    <!-- toggle_targets_private — converging PRIVATE toggle over the picked creatures -->
    <div id="ft-twg-combat-5" class="p-1">
        <button
            id="btn_toggle_targets_private"
            class="btn btn-sm btn-danger"
            data-ft-turn-widget-action="gm-target-action"
            data-ft-url="${togglePrivateUrl}"
            data-ft-target="div_sidelog_warning"
            data-ft-target-action-key="toggle-targets-private"
            data-ft-target-action-label="Toggle target private"
            data-ft-target-action-state="private"
        >${iconMap('toggle_targets_private')}</button>
    </div>


</div>
`;
}


////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Row 4 — Encounter content gates + Campaign features.
//
// Party long rest sits here beside hunger/thirst and waking weariness — moved out of the combat row on
// 2026-09-28 (owner: resting is not a combat control). The browser long-rest lane in
// websocket/long-rest-truth.ts refuses the action while in_combat == 1.
//
// Divider visibility ($show_encounter_desc_gm_amb_divider in the PHP) is the OR of 6 fields:
// encounter_gm_notes, encounter_description, encounter_mx, encounter_amb, encounter_amb_night,
// encounter_amb_weather. encounter_mx does not drive a visible button itself but is preserved
// in the gate to match PHP byte-for-byte.
//
// Ambience buttons carry data-ambience-button="1" + data-audio-id="<uuid>" + a hidden
// .ambience-playing-badge span that the audio playback subsystem (refreshAmbiencePlayingIndicator
// in side-panels-init.js) toggles visible when its track is the active one.
////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export function ftTwgRenderRow4EncounterContentCampaignFeatures(enc: EncounterArr, cam: CampaignArr | null): string {
    const eid = _ftTwgEsc(_ftTwgVal(enc, 'encounter_id'));
    const cid = _ftTwgEsc(_ftTwgVal(cam, 'campaign_id'));

    const hungerToggleUrl  = BASE_URL + 'options/tools/campaigns/toggle_hunger_thirst_enabled_inc.php';
    const wakingToggleUrl  = BASE_URL + 'options/tools/campaigns/toggle_waking_weariness_enabled_inc.php';
    const playersUrl       = BASE_URL + 'tools/campaigns/my_campaign_players.php?campaign_id=' + cid;
    const awardXpFormUrl   = BASE_URL + 'options/tools/campaigns/form_award_experience_marks_inc.php';

    const hungerState = parseInt(_ftTwgVal(cam, 'hunger_thirst_enabled') || '0', 10);
    const wakingState = parseInt(_ftTwgVal(cam, 'waking_weariness_enabled') || '0', 10);
    const hungerEnabledStyle  = hungerState ? 'inline-block' : 'none';
    const hungerDisabledStyle = hungerState ? 'none' : 'inline-block';
    const wakingEnabledStyle  = wakingState ? 'inline-block' : 'none';
    const wakingDisabledStyle = wakingState ? 'none' : 'inline-block';
    const inCombat = String(_ftTwgVal(enc, 'in_combat') || '0');

    return `
<!-- FOURTH ROW -->
<div class="d-flex justify-content-start align-items-center mb-1" data-ft-gm-control-group="world">

    <!-- award_luck -->
    <div class="p-1">
        <button
            class="btn btn-sm btn-primary"
            data-ft-turn-widget-action="gm-target-action"
            data-ft-target="div_sidelog_warning"
            data-ft-target-action-key="award-luck"
            data-ft-target-action-label="Award Luck"
        >${iconMap('award_luck')}</button>
    </div>


    <!-- DIVIDER -->
    <hr class="border border-light" style="width: 1px; height: 20px; display: inline-block;">


    <!-- hunger_thirst_enabled -->
    <div class="p-1">
        <!-- Enabled Button (Visible when hunger_thirst_state == 1) -->
        <button
            id="hunger_thirst_enabled_btn"
            class="btn btn-sm btn-light border"
            style="display: ${hungerEnabledStyle};"
            data-ft-turn-widget-action="toggle-campaign-state"
            data-ft-url="${hungerToggleUrl}"
            data-ft-campaign-id="${cid}"
            data-ft-encounter-id="${eid}"
            data-ft-field="hunger_thirst_enabled"
            data-ft-value="0"
        >${iconMap('hunger_thirst_enabled')}</button>

        <!-- Disabled Button (Visible when hunger_thirst_state == 0) -->
        <button
            id="hunger_thirst_disabled_btn"
            class="btn btn-sm btn-dark border"
            style="display: ${hungerDisabledStyle};"
            data-ft-turn-widget-action="toggle-campaign-state"
            data-ft-url="${hungerToggleUrl}"
            data-ft-campaign-id="${cid}"
            data-ft-encounter-id="${eid}"
            data-ft-field="hunger_thirst_enabled"
            data-ft-value="1"
        >${iconMap('hunger_thirst_disabled')}</button>
    </div>


    <!-- waking_weariness_enabled -->
    <div class="p-1">
        <!-- Enabled Button (Visible when waking_weariness_enabled_state == 1) -->
        <button
            id="waking_weariness_enabled_btn"
            class="btn btn-sm btn-light border"
            style="display: ${wakingEnabledStyle};"
            data-ft-turn-widget-action="toggle-campaign-state"
            data-ft-url="${wakingToggleUrl}"
            data-ft-campaign-id="${cid}"
            data-ft-encounter-id="${eid}"
            data-ft-field="waking_weariness_enabled"
            data-ft-value="0"
        >${iconMap('waking_weariness_enabled')}</button>

        <!-- Disabled Button (Visible when waking_weariness_enabled_state == 0) -->
        <button
            id="waking_weariness_disabled_btn"
            class="btn btn-sm btn-dark border"
            style="display: ${wakingDisabledStyle};"
            data-ft-turn-widget-action="toggle-campaign-state"
            data-ft-url="${wakingToggleUrl}"
            data-ft-campaign-id="${cid}"
            data-ft-encounter-id="${eid}"
            data-ft-field="waking_weariness_enabled"
            data-ft-value="1"
        >${iconMap('waking_weariness_disabled')}</button>
    </div>


    <!-- long_rest_party -->
    <div class="p-1">
        <button
            class="btn btn-sm btn-info"
            id="btn_show_long_rest_party"
            ${inCombat === '1' ? 'disabled title="End combat before taking a party long rest."' : ''}
            data-ft-turn-widget-action="long-rest-show"
        >${iconMap('long_rest_party')}</button>

        <button
            class="btn btn-sm btn-danger"
            id="btn_long_rest_party"
            ${inCombat === '1' ? 'disabled' : ''}
            data-ft-turn-widget-action="long-rest-confirm"
            style="display:none;"
        >${iconMap('confirm_long_rest_party')}</button>
    </div>


    <!-- DIVIDER -->
    <hr class="border border-light" style="width: 1px; height: 20px; display: inline-block;">


    <!-- view_campaign_players: the player table opens as a Workspace tool, never a new tab (owner 2026-10-14) -->
    <div class="p-1">
        <button
            type="button"
            class="btn btn-sm btn-secondary"
            data-ft-turn-widget-action="view-campaign-players"
            data-ft-campaign-id="${cid}"
            data-ft-url="${playersUrl}"
            aria-label="View campaign players"
        >${iconMap('view_campaign_players')}</button>
    </div>


    <!-- DIVIDER -->
    <hr class="border border-light" style="width: 1px; height: 20px; display: inline-block;">


    <!-- award_experience_points -->
    <div class="p-1">
        <button
            class="btn btn-sm btn-primary"
            data-ft-turn-widget-action="open-side-form"
            data-ft-url="${awardXpFormUrl}"
        >${iconMap('award_experience_marks')}</button>
    </div>


</div>
`;
}


////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Row 5 — Combat-only turn timer row (gated on in_combat == 1; empty string otherwise).
//
// auto_end_turn state is read from cookie_auto_end_turn_off via template.js helpers
// (isCookieSet + getCookieValue). Matches the PHP _COOKIE check that previously gated
// which of the on/off button pair was visible on load. Inline onclick handlers mutate
// the cookie via setToggleCookieAutoEndTurn().
////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export function ftTwgRenderRow5CombatTurnTimer(enc: EncounterArr, cam: CampaignArr | null): string {
    if (String(_ftTwgVal(enc, 'in_combat')) !== '1') return '';

    const currentRound = _ftTwgEsc(_ftTwgVal(enc, 'current_round'));
    const defaultTimer = _ftTwgEsc(_ftTwgVal(cam, 'default_creature_turn_timer'));

    const autoEndTurnOff =
        typeof isCookieSet === 'function'
        && isCookieSet('cookie_auto_end_turn_off')
        && String(getCookieValue('cookie_auto_end_turn_off')) === '1';
    const displayOff = autoEndTurnOff ? '' : 'style="display: none;"';
    const displayOn  = autoEndTurnOff ? 'style="display: none;"' : '';
    return `
<!-- COMBAT CONTROLS ROW -->
<div class="d-flex justify-content-start align-items-center mb-1" data-ft-gm-control-group="turn">


    <!-- go_to_creature_turn -->
    <div class="p-1">
        <button
            class="btn btn-sm btn-primary"
            data-ft-turn-widget-action="call"
            data-ft-method="goToCreatureTurn"
        >${iconMap('go_to_creature_turn')}</button>
    </div>


    <!-- PREVIOUS TURN — during a Legendary Turn the server closes the interlude, restores
         the pending Main-Turn anchor, and then rewinds one ordinary initiative slot. -->
    <div class="p-1">
        <button
            id="btn_previous_turn"
            class="btn btn-sm btn-primary"
            data-ft-turn-widget-action="call"
            data-ft-method="gmControlsPreviousTurn"
        >${iconMap('previous_turn', 25, 25)}</button>
    </div>


    <!-- CURRENT ROUND -->
    <div class="p-1" id="div_encounter_current_round">
        <h3 class="mt-0"><span id="span_encounter_current_round" class="badge rounded-pill bg-success text-dark border border-dark">${currentRound}</span></h3>
    </div>


    <!-- TIME REMAINING -->
    <div class="p-1" id="div_encounter_creature_turn_timer_remaining">
        <h3 class="mt-0">
            <span
                id="span_creature_turn_timer_remaining"
                class="badge bg-warning text-dark"
                style="min-width:50px;"
            >${defaultTimer}</span>
        </h3>
    </div>


    <!-- PAUSE PLAY TURN (default is off) -->
    <div class="p-1">
        <button
            id="btn_pause_turn_timer"
            class="btn btn-sm btn-secondary"
            style="display: none;"
            data-ft-turn-widget-action="call"
            data-ft-method="toggleTurnTimer"
            data-ft-args="false"
        >${iconMap('pause_turn_timer')}</button>

        <button
            id="btn_resume_turn_timer"
            class="btn btn-sm btn-dark border"
            data-ft-turn-widget-action="call"
            data-ft-method="toggleTurnTimer"
            data-ft-args="true"
        >${iconMap('resume_turn_timer')}</button>
    </div>


    <!-- RESET TURN TIMER -->
    <div class="p-1">
        <button
            id="btn_reset_turn_timer"
            class="btn btn-sm btn-warning"
            data-ft-turn-widget-action="call"
            data-ft-method="resetTurnTimer"
        >${iconMap('reset_turn_timer')}</button>
    </div>


    <!-- ADD 10 SECONDS TO TURN TIMER -->
    <div class="p-1">
        <button
            type="button"
            id="btn_add_turn_timer_10_seconds"
            class="btn btn-sm btn-info"
            data-ft-turn-widget-action="call"
            data-ft-method="addTurnTimerSeconds"
            data-ft-args="10"
        >${iconMap('add_turn_timer_seconds')}</button>
    </div>


    <!-- NEXT TURN -->
    <div class="p-1">
        <button
            id="btn_next_turn"
            class="btn btn-sm btn-primary"
            data-ft-turn-widget-action="call"
            data-ft-method="gmControlsNextTurn"
        >${iconMap('next_turn')}</button>
    </div>


    <!-- AUTO GO TO NEXT TURN -->
    <div class="p-1">
        <button
            id="btn_auto_end_turn_off"
            class="btn btn-sm btn-dark border"
            role="button"
            type="button"
            data-ft-turn-widget-action="auto-end-turn"
            data-ft-auto-end-turn="0"
            ${displayOff}
        >${iconMap('auto_end_turn_off')}</button>

        <button
            id="btn_auto_end_turn_on"
            class="btn btn-sm btn-success border"
            role="button"
            type="button"
            data-ft-turn-widget-action="auto-end-turn"
            data-ft-auto-end-turn="1"
            ${displayOn}
        >${iconMap('auto_end_turn_on')}</button>
    </div>


</div>
`;
}


////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Aggregate renderer — returns the HTML for the entire turn-widget GM controls.
// All 5 original rows ported, plus the `links` row split out of row 2 on 2026-09-02.
// The PHP partial turn_widget_gm_controls_inc.php has been retired
// to _ai_code_files/retired_php/turn_widget/ for reference.
////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export function renderTurnWidgetGMControls(enc: EncounterArr | null | undefined, cam: CampaignArr | null | undefined): string {
    if (!enc || !encounterField(enc, 'encounter_id')) return '';
    const camOrNull = cam ?? null;
    return ftTwgRenderHiddenInputs(enc) + `
<section class="ft-campaign-gm-controls" aria-label="GM controls">
    <div class="ft-campaign-gm-control-rows">
        ${ftTwgRenderRow1EncounterActions(enc, camOrNull)}
        ${ftTwgRenderRowEditEncounter(enc, camOrNull)}
        ${ftTwgRenderRow2EncounterNavCampaignState(enc, camOrNull)}
        ${ftTwgRenderRowLinks(enc, camOrNull)}
        ${ftTwgRenderRow3CombatVisibility(enc, camOrNull)}
        ${ftTwgRenderRow4EncounterContentCampaignFeatures(enc, camOrNull)}        ${ftTwgRenderRow5CombatTurnTimer(enc, camOrNull)}
        ${ftTwgRenderRowAdmin(enc, camOrNull)}
    </div>
</section>`;
}

// Keep scalar dependency values, never the encounter/campaign object: both are patched in place.
// The markup registry belongs to post-template-late; use its bridge instead of duplicating it here.
const _ftTwgPaints = new WeakMap<HTMLElement, { encounterId: string; dependencies: Map<string, string> }>();

/** Returns true only when a timer was inserted and needs its initial state mirrored. */
export function patchTurnWidgetGMControls(root: HTMLElement, enc: EncounterArr | null | undefined, cam: CampaignArr | null | undefined): boolean {
    const page = window as unknown as {
        ftPatchMarkup: (host: HTMLElement, html: string, inserted: (host: HTMLElement) => void) => void;
        ftRememberPatchMarkup: (host: HTMLElement) => void;
    };
    const encounterId = _ftTwgVal(enc, 'encounter_id');
    if (!enc || !encounterId) {
        if (root.childNodes.length) replaceHydratedHtml(root, '');
        _ftTwgPaints.delete(root);
        return false;
    }
    let painted = _ftTwgPaints.get(root);
    const initial = !painted || painted.encounterId !== encounterId || !root.querySelector('.ft-campaign-gm-control-rows');
    if (initial) {
        replaceHydratedHtml(root, renderTurnWidgetGMControls(enc, cam));
        painted = { encounterId, dependencies: new Map() };
        _ftTwgPaints.set(root, painted);
    }
    const rows = root.querySelector<HTMLElement>('.ft-campaign-gm-control-rows')!;
    const campaign = cam ?? null;
    const read = (keys: string[], value: EncounterArr | CampaignArr | null = enc): string[] => keys.map(key => _ftTwgVal(value, key));
    const campaignId = _ftTwgVal(campaign, 'campaign_id');
    const inCombat = _ftTwgVal(enc, 'in_combat') || '0';
    const editBezel = document.querySelector('[data-ft3d-edit-mode]');
    const editMode = _ftTwgCall('ftIsFt3dEditMode') ?? editBezel?.getAttribute('aria-pressed');
    const slots: [string, unknown[], () => string][] = [
        ['encounter', [...read(['encounter_description', 'encounter_gm_notes']), campaignId,
            _ftTwgVal(campaign, 'campaign_ft3d_mode'), !!document.querySelector('[data-ft3d-session-mode]')], () => ftTwgRenderRow1EncounterActions(enc, campaign)],
        ['edit_encounter', [campaignId, !!editBezel, editMode], () => ftTwgRenderRowEditEncounter(enc, campaign)],
        ['campaign', [...read(['encounter_location_id', 'encounter_location_map', 'encounter_location_parent_id', 'location_parent_map']),
            _ftTwgFirstEncounterPicUrl(enc), ...read(['campaign_id', 'campaign_disable_player_haven_return', 'campaign_downtime_enabled', 'campaign_stronghold_encounter_id'], campaign)],
            () => ftTwgRenderRow2EncounterNavCampaignState(enc, campaign)],
        ['links', [campaignId], () => ftTwgRenderRowLinks(enc, campaign)],
        ['combat', [inCombat], () => ftTwgRenderRow3CombatVisibility(enc, campaign)],
        ['world', [inCombat, ...read(['campaign_id', 'hunger_thirst_enabled', 'waking_weariness_enabled'], campaign)],
            () => ftTwgRenderRow4EncounterContentCampaignFeatures(enc, campaign)],
        // Round and countdown have their own live nodes; neither invalidates timer controls.
        ['turn', [inCombat], () => ftTwgRenderRow5CombatTurnTimer(enc, campaign)],
        ['admin', [_ftTwgViewerIsStaff()], () => ftTwgRenderRowAdmin(enc, campaign)],
    ];
    const hadTimer = !initial && !!root.querySelector('#span_creature_turn_timer_remaining');
    let previousHost: HTMLElement | null = null;
    for (const [key, values, render] of slots) {
        const signature = JSON.stringify(values);
        let host = rows.querySelector<HTMLElement>(`[data-ft-gm-slot="${key}"]`)
            ?? (initial ? rows.querySelector<HTMLElement>(`[data-ft-gm-control-group="${key}"]`) : null);
        if (painted!.dependencies.get(key) === signature) { previousHost = host; continue; }
        if (!host) { host = document.createElement('div'); rows.insertBefore(host, previousHost ? previousHost.nextSibling : rows.firstChild); }
        previousHost = host;
        host.dataset.ftGmSlot = key;
        if (initial) {
            page.ftRememberPatchMarkup(host);
            painted!.dependencies.set(key, signature);
            continue;
        }
        const template = document.createElement('template'); template.innerHTML = render();
        const row = template.content.firstElementChild;
        // Keep the actual groups as direct children: the campaign shell owns their hidden/tab
        // state and its CSS uses that relationship. Never overwrite the shell's live `hidden`.
        for (const attr of [...host.attributes]) {
            if (attr.name !== 'data-ft-gm-slot' && attr.name !== 'hidden' && !row?.hasAttribute(attr.name)) host.removeAttribute(attr.name);
        }
        for (const attr of row?.attributes ?? []) if (host.getAttribute(attr.name) !== attr.value) host.setAttribute(attr.name, attr.value);
        page.ftPatchMarkup(host, row?.innerHTML ?? '', hydrateSubtree);
        painted!.dependencies.set(key, signature);
    }
    if (initial) hydrateSubtree(root);
    const pointer = root.querySelector<HTMLInputElement>('#input_hidden_gm_controls_current_encounter_creature_id_turn');
    const currentTurn = _ftTwgVal(enc, 'current_encounter_creature_id_turn');
    if (pointer && pointer.value !== currentTurn) pointer.value = currentTurn;
    const round = root.querySelector('#span_encounter_current_round');
    const currentRound = _ftTwgVal(enc, 'current_round');
    if (round && round.textContent !== currentRound) round.textContent = currentRound;
    return !hadTimer && !!root.querySelector('#span_creature_turn_timer_remaining');
}
